import React from 'react';
import {
  Box,
  Button,
  ButtonBase,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  ExpandLessRounded as ExpandLessRoundedIcon,
  ExpandMoreRounded as ExpandMoreRoundedIcon,
  ManageAccountsOutlined as ManageAccountsOutlinedIcon,
} from '@mui/icons-material';
import colors from '../../assets/theme/colors';
import { useLocation, useNavigate } from 'react-router-dom';
import SourceOriginalNotePanel from '../../components/Timeline/SourceOriginalNotePanel';
import { canManageCareRecordPrivacy } from '../../services/timeline/recordVisibility';
import { PROFILE_REVIEW_REQUEST_EVENT } from './ProfileReviewQueue';
import { getCareSourceOriginalText } from './careSourceStatusModel';
import {
  getProfileUpdateReceiptSummary,
  getProfileUpdateReceiptTitle,
} from './profileUpdateTimelineReceiptModel';
import { buildMedicationReviewNavigationState } from '../../utils/medicationReviewNavigation';

const formatCapturedAt = (millis = 0) => {
  if (!millis) return '';
  const date = new Date(millis);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

const ProfileUpdateTimelineSection = ({
  receipts = [],
  child = null,
  currentUserRole = null,
}) => {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const personName = child?.preferredName || child?.name || child?.firstName || 'Profile';
  const canReview = canManageCareRecordPrivacy(currentUserRole);

  if (!receipts.length) return null;

  const dailySummary = receipts.reduce((summary, receipt) => ({
    savedDetailCount: summary.savedDetailCount + Number(receipt.savedDetailCount || 0),
    pendingTotal: summary.pendingTotal + Number(receipt.pendingTotal || 0),
    medicationReviewCount: summary.medicationReviewCount + Number(receipt.medicationReviewCount || 0),
  }), { savedDetailCount: 0, pendingTotal: 0, medicationReviewCount: 0 });
  const medicationOnly = receipts.every((receipt) => receipt.isMedicationSource);

  const requestReview = () => {
    if (typeof window === 'undefined') return;
    setIsExpanded(false);
    window.dispatchEvent(new CustomEvent(PROFILE_REVIEW_REQUEST_EVENT, {
      detail: {
        childId: child?.id || '',
        sourceIds: receipts
          .filter((receipt) => receipt.pendingTotal > 0)
          .flatMap((receipt) => receipt.sources || [])
          .map((source) => source.id)
          .filter(Boolean) || [],
      },
    }));
  };

  return (
    <Box sx={{ px: { xs: 0, md: 0 }, mb: 1.2 }}>
      <Typography sx={{ px: { xs: 1.35, md: 1.65 }, mb: 0.65, color: alpha(colors.semantic.ink, 0.64), fontSize: '0.76rem', fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
        {medicationOnly ? 'Medication activity' : 'Care Profile activity'}
      </Typography>
      <Paper
        variant="outlined"
        sx={{ borderRadius: '18px', borderColor: alpha(colors.semantic.sageDeep, 0.26), overflow: 'hidden', bgcolor: '#FFFFFF', boxShadow: 'none' }}
      >
            <ButtonBase
              onClick={() => setIsExpanded((current) => !current)}
              aria-expanded={isExpanded}
              sx={{ width: '100%', px: { xs: 1.25, md: 1.5 }, py: 1.1, textAlign: 'left', justifyContent: 'flex-start' }}
            >
              <Box sx={{ width: 38, height: 38, mr: 1.1, borderRadius: '14px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', bgcolor: alpha(colors.semantic.sageDeep, 0.1), color: colors.semantic.sageDeep, flexShrink: 0 }}>
                <ManageAccountsOutlinedIcon sx={{ fontSize: 21 }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ color: colors.semantic.ink, fontSize: '0.98rem', fontWeight: 750, lineHeight: 1.25 }}>
                  {medicationOnly ? 'Medication document uploaded' : getProfileUpdateReceiptTitle(dailySummary, personName)}
                </Typography>
                <Typography sx={{ mt: 0.2, color: colors.semantic.ink2, fontSize: '0.8rem', lineHeight: 1.35 }}>
                  {medicationOnly
                    ? `${dailySummary.medicationReviewCount} medication${dailySummary.medicationReviewCount === 1 ? '' : 's'} ready for review`
                    : getProfileUpdateReceiptSummary(dailySummary)}
                </Typography>
                <Typography sx={{ mt: 0.25, color: colors.semantic.sageDeep, fontSize: '0.74rem', fontWeight: 800, lineHeight: 1.35 }}>
                  {medicationOnly ? 'Medication source · Not a dose event' : 'Care Profile activity · Not a Timeline event'}
                </Typography>
              </Box>
              {isExpanded ? (
                <ExpandLessRoundedIcon sx={{ ml: 0.75, color: colors.semantic.sageDeep }} />
              ) : (
                <ExpandMoreRoundedIcon sx={{ ml: 0.75, color: colors.semantic.sageDeep }} />
              )}
            </ButtonBase>
            {isExpanded ? (
              <>
                <Divider />
                <Stack
                  aria-label="Profile update details"
                  spacing={1.25}
                  divider={<Divider flexItem />}
                  sx={{ px: { xs: 1.5, sm: 2 }, py: 1.5 }}
                >
                  {dailySummary.pendingTotal > 0 && canReview ? (
                    <Button variant="contained" onClick={requestReview} sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: 750, boxShadow: 'none' }}>
                      Review profile details
                    </Button>
                  ) : null}
                  {receipts.map((receipt) => {
                    const originalSource = receipt.sources?.[0] || {};
                    const originalText = getCareSourceOriginalText(originalSource);
                    const capturedAtLabel = formatCapturedAt(receipt.capturedAtMillis);

                    return (
                      <Box key={receipt.id}>
                        <Typography sx={{ mb: 0.75, color: colors.semantic.ink2, fontSize: '0.76rem', fontWeight: 800 }}>
                          {capturedAtLabel}{capturedAtLabel ? ' · ' : ''}{receipt.isMedicationSource
                            ? `${receipt.medicationReviewCount} medication${receipt.medicationReviewCount === 1 ? '' : 's'} ready for review`
                            : getProfileUpdateReceiptSummary(receipt)}
                        </Typography>
                        {receipt.isMedicationSource && receipt.medicationReviewCount > 0 && canReview ? (
                          <Button
                            variant="contained"
                            onClick={() => navigate(
                              `/children/${child?.id}/medications/review/${originalSource.id}`,
                              {
                                state: {
                                  source: originalSource,
                                  medicationReviewNavigation: buildMedicationReviewNavigationState({
                                    childId: child?.id || '',
                                    returnTo: location.pathname,
                                    originDate: receipt.capturedAtMillis,
                                    originSourceId: originalSource.id,
                                  }),
                                },
                              }
                            )}
                            sx={{ mb: 1, textTransform: 'none', fontWeight: 750, boxShadow: 'none' }}
                          >
                            Review {receipt.medicationReviewCount} medication{receipt.medicationReviewCount === 1 ? '' : 's'}
                          </Button>
                        ) : null}
                        <SourceOriginalNotePanel
                          originalNote={originalText}
                          sourceNoteId={originalSource.id || receipt.sourceKey}
                          sourceNotePath={originalSource.path || ''}
                          captureDetailsKey={receipt.sourceKey}
                          captureDetailsChildId={child?.id || ''}
                          showSourceReviewActions={false}
                          showGenericCareDetails={false}
                          showOriginalNoteSection
                          variant="inline"
                        />
                      </Box>
                    );
                  })}
                </Stack>
              </>
            ) : null}
      </Paper>
    </Box>
  );
};

export default ProfileUpdateTimelineSection;
